<template>
  <DxTabPanel tabs-position="top" icon-position="left">
    <DxItem :key="1" :title="$t('facturas.modal_detalle.tab_datos')" class="tab-item" icon="fa-regular fa-file-lines">
      <FacturaModalDetailTabDatos :model="model" />
    </DxItem>
    <DxItem :key="2" :title="$t('facturas.modal_detalle.tab_documentos')" class="tab-item" icon="fa-regular fa-folder-open">
      <FacturaModalDetailTabDocumentos :id-factura="idFactura" :model="model" @updated-factura="onFacturaUpdated" />
    </DxItem>
    <DxItem :key="3" :title="$t('facturas.modal_detalle.tab_desglose')" class="tab-item" icon="fa-regular fa-table">
      <FacturaModalDetailTabDesglose :id-factura="idFactura" :model="model" @updated-factura="onFacturaUpdated" />
    </DxItem>
  </DxTabPanel>
</template>

<script setup lang="ts">
import { computed, getCurrentInstance, nextTick, onMounted, ref } from "vue";
import DxTabPanel, { DxItem } from "devextreme-vue/tab-panel";
import type { FacturaDetailModel } from "@api/api";
import { useApi } from "@api/integration";
import { useIntl } from "@services/i18n";
import { useModalBuilder } from "@/.shared/helpers/modal-building-helper";
import { dangerButtonProps, defaultButtonProps, primaryButtonProps, warningButtonProps } from "@helpers/button-helper";
import { messageBoxResult } from "@/.shared/components/dialogs/message-box";
import { toastError, toastSuccess } from "@/.shared/services/notification-service";
import { ApiError } from "@/.shared/services/http";
import { GENERAL_NOT_FOUND } from "@/.shared/errors/codes";
import { useAuthorization } from "@/.shared/services/auth/authorization";
import { useModalService } from "@services/modal-service";
import { useRouter } from "@/.shared/services/routing-services";
import FacturaModalDetailTabDatos from "./FacturaModalDetailTabDatos.vue";
import FacturaModalDetailTabDocumentos from "./FacturaModalDetailTabDocumentos.vue";
import FacturaModalDetailTabDesglose from "./FacturaModalDetailTabDesglose.vue";
import { useGrupoUsuarioChecker } from "@services/auth/authorization/grupo-usuario-checker";

const props = defineProps<{
  idFactura: string;
  idTicket?: string;
}>();

const api = useApi();
const intl = useIntl();
const modalService = useModalService();
const autho = useAuthorization();
const router = useRouter();
const { esAgente, esBackoffice } = useGrupoUsuarioChecker();
const modal = useModalBuilder();
modal.setContext(getCurrentInstance()!);

const model = ref<FacturaDetailModel | null>(null);

const puedeModificarFactura = computed(() => esBackoffice());
const puedeAceptarRevision = computed(() => esAgente());

const codigoFactura = computed(() => model.value?.Identificador ?? model.value?.IdentificadorBorrador);

function setModalTitle(): void {
  const codigo = codigoFactura.value;
  modal.setPopupOptions({
    title: intl.i18n.t("facturas.modal_detalle.titulo_con_codigo", { codigo }),
  });
}

async function loadFactura(): Promise<void> {
  try {
    model.value = await api.callStandardApi("getFacturaDetailById", { showLoading: true }, props.idFactura);    
  } catch (e: unknown) {
    if (e instanceof ApiError && e.details?.errorList.length === 1 && [GENERAL_NOT_FOUND].includes(e.details.errorList[0].Code)) {
      toastError(intl.i18n.t("facturas.modal_detalle.no_encontrada"));
      modal.close(messageBoxResult.cancel);
    } else {
      throw e;
    }
  }
}

onMounted(async () => {
  await loadFactura();
  configureButtons();

  await nextTick();
  setModalTitle();
});

function configureButtons(): void {
  modal.remove("facturaAceptarRevision");
  modal.remove("facturaRechazarRevision");
  modal.remove("facturaLiquidar");
  modal.remove("facturaRechazar");
  modal.remove("facturaMarcarPendienteRevision");
  modal.remove("facturaIrSoporte");

  if (puedeModificarFactura.value && (model.value!.Estado === "BORRADOR" || model.value!.Estado === "PDT_REVISION_COLABORADOR")) {
    addBackofficeButtons();
  }

  if (puedeAceptarRevision.value
    && model.value!.Estado === "PDT_REVISION_COLABORADOR"
    && model.value!.ImporteTotal >= 0) {
    addColaboradorButtons();
  }

  if (!!model.value?.IdTicketAbierto && model.value.IdTicketAbierto !== props.idTicket) {
    addIrSoporteButton();
  }

  if (!modal.hasButton("closeBottom")) {
    const closeButton = modal.standardButtons.addClose();
    closeButton.update((w) => w.onClick = () => modal.close(messageBoxResult.cancel));
  }
}

function addIrSoporteButton(): void {
  const irSoporteBtn = modal.addButton(
    "facturaIrSoporte",
    { ...primaryButtonProps(intl.i18n.t("facturas.modal_detalle.btn_ir_soporte"), true), icon: "fa-regular fa-headset" },
    { toolbar: "bottom", location: "after" },
  );

  irSoporteBtn.update((w) => w.onClick = async () => await irASoporte());
}

async function irASoporte(): Promise<void> {
  const idTicket = model.value?.IdTicketAbierto;
  if (!idTicket) return;

  modal.close(messageBoxResult.cancel);
  await router.push(intl.i18Route({ name: "ticket", params: { "id": idTicket } }));
}

function addColaboradorButtons(): void {
  const aceptarRevisionBtn = modal.addButton(
    "facturaAceptarRevision",
    primaryButtonProps(intl.i18n.t("facturas.modal_detalle.btn_aceptar")),
    { toolbar: "bottom", location: "before" },
  );

  aceptarRevisionBtn.update((w) => w.onClick = async () => await aceptarRevision());

  const rechazarRevisionBtn = modal.addButton(
    "facturaRechazarRevision",
    dangerButtonProps(intl.i18n.t("facturas.modal_detalle.btn_rechazar")),
    { toolbar: "bottom", location: "before" },
  );

  rechazarRevisionBtn.update((w) => w.onClick = async () => await rechazarRevision());
}

function addBackofficeButtons(): void {
  const esBorrador = model.value!.Estado === "BORRADOR";
  const esRevision = model.value!.Estado === "PDT_REVISION_COLABORADOR";
  const esRectificativaEnRevision = esRevision && model.value!.ImporteTotal < 0;

  if (esBorrador || esRectificativaEnRevision) {
    const liquidarBtn = modal.addButton(
      "facturaLiquidar",
      primaryButtonProps(intl.i18n.t("facturas.modal_detalle.btn_liquidar")),
      { toolbar: "bottom", location: "before" },
    );

    liquidarBtn.update((w) => w.onClick = async () => await liquidarFactura());

    if (esBorrador) {
      const enviarRevisionBtn = modal.addButton(
        "facturaMarcarPendienteRevision",
        defaultButtonProps(intl.i18n.t("facturas.modal_detalle.btn_enviar_revision")),
        { toolbar: "bottom", location: "before" },
      );

      enviarRevisionBtn.update((w) => w.onClick = async () => await marcarPendienteRevisionColaborador());
    }
  }

  if (esBorrador || esRevision) {
    const rechazarBtn = modal.addButton(
      "facturaRechazar",
      dangerButtonProps(intl.i18n.t("facturas.modal_detalle.btn_rechazar")),
      { toolbar: "bottom", location: "before" },
    );

    rechazarBtn.update((w) => w.onClick = async () => await rechazarFactura());
  }
}

async function confirmarSiTicketAbierto(): Promise<boolean> {
  if (!model.value?.IdTicketAbierto) return true;

  const respuesta = await modalService.messageBox(
    intl.i18n.t("facturas.modal_detalle.aviso_ticket_abierto_mensaje"),
    intl.i18n.t("facturas.modal_detalle.aviso_ticket_abierto_titulo"),
    "okCancel",
    "warning",
  );
  return respuesta === messageBoxResult.ok;
}

async function liquidarFactura(): Promise<void> {
  if (!(await confirmarSiTicketAbierto())) return;

  try {
    await api.callStandardApi("settleFactura", { showLoading: true }, props.idFactura);
    toastSuccess(intl.i18n.t("facturas.modal_detalle.liquidar_exito"));
    await onFacturaUpdated();
  } catch {
    toastError(intl.i18n.t("facturas.modal_detalle.liquidar_fallo"));
  }
}

async function marcarPendienteRevisionColaborador(): Promise<void> {
  try {
    await api.callStandardApi("markPendingReviewFactura", { showLoading: true }, props.idFactura);
    toastSuccess(intl.i18n.t("facturas.modal_detalle.enviar_revision_exito"));
    await onFacturaUpdated();
  } catch {
    toastError(intl.i18n.t("facturas.modal_detalle.enviar_revision_fallo"));
  }
}

async function rechazarFactura(): Promise<void> {
  if (!(await confirmarSiTicketAbierto())) return;

  const motivoRechazo = await modalService.inputBox(`${intl.i18n.t("facturas.modal_detalle.motivo_rechazo")} ${intl.i18n.t("facturas.modal_detalle.pregunta_rechazar")} ${codigoFactura.value}`, 'dxTextArea', null, true);
  if (motivoRechazo === undefined) return;

  let respuesta = await modalService.messageBox(intl.i18n.t('facturas.modal_detalle.rechazar_pregunta'), intl.i18n.t('facturas.modal_detalle.titulo_confirma_rechazar'), "okCancel", "warning");
  if (respuesta !== messageBoxResult.ok) return;

  try {
    await api.callStandardApi("rejectFactura", { showLoading: true }, props.idFactura, motivoRechazo!);
    toastSuccess(intl.i18n.t('facturas.modal_detalle.rechazar_exito'));
    await onFacturaUpdated();
  } catch {
    toastError(intl.i18n.t('facturas.modal_detalle.rechazar_fallo'));
  }
}

async function aceptarRevision(): Promise<void> {
  try {
    await api.callStandardApi("settlePendingReviewFactura", { showLoading: true }, props.idFactura);
    toastSuccess(intl.i18n.t("facturas.modal_detalle.aceptar_revision_exito"));
    await onFacturaUpdated();
  } catch {
    toastError(intl.i18n.t("facturas.modal_detalle.aceptar_revision_fallo"));
  }
}

async function rechazarRevision(): Promise<void> {
  const motivoRechazo = await modalService.inputBox(`${intl.i18n.t("facturas.modal_detalle.motivo_rechazo")} ${intl.i18n.t("facturas.modal_detalle.pregunta_rechazar")} ${codigoFactura.value}`, 'dxTextArea', null, true);  
  if (motivoRechazo == null) return;
  
  try {
    await api.callStandardApi("rejectPendingReviewFactura", { showLoading: true }, props.idFactura, motivoRechazo!);
    toastSuccess(intl.i18n.t("facturas.modal_detalle.rechazar_revision_exito"));
    await onFacturaUpdated();
  } catch {
    toastError(intl.i18n.t("facturas.modal_detalle.rechazar_fallo"));
  }
}

async function onFacturaUpdated(): Promise<void> {
  await loadFactura();
  configureButtons();
}
</script>
